/**
 * Specials page.
 *
 * @module app/pages/SpecialsPage
 */
import _ from 'lodash';
import $ from 'jquery';
import BasePage from './BasePage';
import BodyStateAdapter from '../components/BodyStateAdapter';
import DomPushAdapter from '../components/push/domUpdates/DomPushAdapter';
import MessageBusTopics from '../../common/MessageBusTopics';
import ModelComposer from '../components/ModelComposer';
import OffCanvas from '../components/OffCanvas';
import PushAdapter from '../components/push/PushAdapter';
import SelectionViewAdapter from '../views/SelectionViewAdapter';
import LiveScore from '../components/LiveScore';
import URLProvider from '../components/URLProvider';

/**
 * OffCanvas Wrapper jquery element object
 *
 * @type {Object}
 *
 * @private
 */
let $offCanvas = null;

/**
 * Internal state flag used to
 * determine whether page has been
 * rendered or not
 *
 * @type {Boolean}
 */
let rendered = false;

/**
 * Page content in the form of a jQuery element
 *
 * @type {Object}
 */
let content = null;

/**
 * Page dependencies
 *
 * @type {Object}
 */
let deps = null;

/**
 * Page arguments
 *
 * @type {Object}
 */
let args = null;

/**
 * Function used to bind event handles to
 * various buttons/links on the page
 *
 * @param   {Object} content jQuery object containing
 *                           the page content
 * @param   {Object} deps    Page dependencies
 * @param   {Object} args    Page arguments
 *
 * @returns {void}
 *
 * @private
 */
const bindNavigation = (content, deps, args) => {

    initInternals(content, deps, args);
    bindCustomNavigation(content);
};

/**
 * Initialize interface
 *
 * @returns {void}
 *
 * @private
 */
const initInterface = () => {

    const model = deps.sportsDataRepository.getModel();

    SelectionViewAdapter.init(model.selections, content);
    _.each(model.events, (event) => LiveScore.subscribe(event, content));
};

/**
 * Init model for later usage
 *
 * @params {Boolean} regenerateModel
 *
 * @returns {void}
 *
 * @private
 */
const initModel = (regenerateModel) => {

    BasePage.setOddsFormat();

    if (regenerateModel === true) {
        ModelComposer.regenerateMergedModel();
    }

    PushAdapter.unsubscribeAll();
    _.each(deps.sportsDataRepository.getModel(), push.subscribeSetToPush);
};

/**
 * Perform initalization of model and interface
 *
 * @params {Boolean} regenerateModel
 *
 * @returns {void}
 *
 * @private
 */
const init = (regenerateModel) => {

    initModel(regenerateModel);
    initInterface();
};

/**
 * Init internal data for later usage
 *
 * @param   {Object} content jQuery container element
 * @param   {Object} deps    Page dependencies
 * @param   {Object} args    Page arguments
 *
 * @returns {void}
 *
 * @private
 */
const initInternals = (pageContent, pageDeps, pageArgs) => {

    content = pageContent;
    deps = pageDeps;
    args = pageArgs;
};

/**
 * Updates page after loading meeting Markets page
 *
 * @returns {void}
 *
 * @private
 */
const meetingMarketsUpdate = () => {

    bindCustomNavigation(content);
};

/**
 * Function used to load the data for a meeting market
 * based on the clicked meetingNav element
 *
 * @returns {void}
 *
 * @private
 */
const meetingMarketsClickAction = function() {

    let meetingMarketsLinkElement = $(this);
    args.meetingMarketsId = meetingMarketsLinkElement.attr('data-meetingMarketsId');

    /**
     * What to do after a successful reload of snippet.
     *
     * @returns {void}
     *
     * @private
     */
    const afterSuccess = () => {

        init(true);
        meetingMarketsUpdate();
        OffCanvas.hideLeftPanel($offCanvas);

        content
            .find('.race-group-content__item-link--selected')
            .removeClass('race-group-content__item-link--selected');
        meetingMarketsLinkElement.addClass('race-group-content__item-link--selected');

        window.WH.messageBus.publish(MessageBusTopics.NAVIGATION_PAGE_CHANGED);
        BasePage.scrollTop();
    };

    /**
     * Pre-render action
     *
     * @param {Object} data
     *
     * @returns {string}
     *
     * @private
     */
    const preRender = (data) => {

        let wrapper = $(data).closest('.race-event-wrapper');

        wrapper.find('#marketCollectionsMenu').remove();

        return wrapper.html();
    };

    const meetingMarketsName =  meetingMarketsLinkElement.data('racename');
    const redirectArgs = _.extend({}, args, { meetingMarketsName });

    // reload the snippet and set the url and history
    deps.pageCommon.reloadSnippet({
        'requestURL': URLProvider.specials.meetingMarkets(args),
        'redirectURL': URLProvider.specials.meetingMarkets(redirectArgs, true),
    }, '.race-event-wrapper', { afterSuccess, preRender });
};

/**
 * Function used for binding custom behavior
 * to buttons or links on the page
 *
 * @param   {Object} content jQuery element
 *
 * @returns {void}
 *
 * @private
 */
const bindCustomNavigation = (content) => {

    const bindings = [{
        selector: '.race-group-content__item-link[data-meetingMarketsId]',
        clickGroup: 'meetingMarkets',
        bindFunctions: [ meetingMarketsClickAction ],
    }];

    _.each(bindings, _.partial(applyBindings, content));
};

/**
 * Binds functions to the given binding Object
 *
 * @param {Object} content jQuery element
 * @param {Object} binding Object with required attributes:
 *                              {String} selector jQuery selector
 *                              {String} clickGroup Event Namespace
 *                              {Array} bindFunctions An Array of functions to bind to a click event
 *
 * @returns {void}
 *
 * @private
 */
const applyBindings = (content, binding) => {

    const selector = binding.selector;
    const clickGroup = binding.clickGroup;

    content.find(selector).off('click.' + clickGroup);

    _.each(binding.bindFunctions, (bindFunction) => {
        content.find(selector).on('click.' + clickGroup, bindFunction);
    });
};

/**
 * Push related functionality
 *
 * @type {Object}
 */
const push = {
    /**
     * Subscribe a set of entities (events, markets, etc)
     * to PUSH updates
     *
     * @param   {Object} set An object containing entities
     *                       such as markets, selections, events
     *
     * @returns {void}
     *
     * @private
     */
    subscribeSetToPush: (set) =>_.each(set, push.subscribeEntityToPush),
    /**
     * Subscribe a single entity to PUSH updates
     *
     * @param   {Object} entity Market/event/selection model
     *
     * @returns {void}
     *
     * @private
     */
    subscribeEntityToPush: (entity) => {

        if (entity && entity.id && !entity.id.match(/OB_TY/g) && !entity.settled &&
            DomPushAdapter.subscribe(entity, push.callback)) {
            PushAdapter.subscribe(entity.pdsTopic);
        }
    },

    /**
     * DOM PUSH update callback function
     * This function is called every time a push
     * message comes in
     *
     * @param {Object} message DOM PUSH message
     *
     * @private
     */
    callback: (message) => {

        if (push.handlers.hasOwnProperty(message.attribute)) {
            push[push.handlers[message.attribute]](message);
        }
    },

    /**
     * PUSH handlers available per attribute type
     *
     * @type {Object}
     */
    handlers: {},
};
/**
 * Contains functions exposed by this component
 * as an API
 *
 * @type {Object}
 */
let api = {

    bindNavigation: bindNavigation,

    /**
     * Return the URL associated with this page
     * based on the args
     *
     * @param   {Object} args Page arguments
     *
     * @returns {String}      Compiled URL based on args
     *
     * @public
     */
    getContentURL: (args) => {

        let base = `/betting/${args.locale}/${args.sport}/specials`;

        if (args.raceId) {
            base = `/betting/${args.locale}/${args.sport}/OB_EV${args.raceId}`;
        }

        return base;
    },

    /**
     * Function called after the DOM ready event
     * is fired
     *
     * @param   {Object} deps Object that contains page
     *                        dependencies
     *
     * @returns {void}
     *
     * @public
     */
    postReady: (deps, content, args) => {

        BodyStateAdapter.init($, 'specials', args.sport);

        if (deps.ready) {
            deps.ready('specials');
        }
    },

    /**
     * Function called after rendering the page
     *
     * @returns {void}
     *
     * @public
     */
    postRender: () => {

        BasePage.switchTab('Specials');
        init();
        meetingMarketsUpdate();
    },

    /**
     * Changes rendered state flag to true
     *
     * @public
     */
    setRendered: () => {

        rendered = true;
        $offCanvas = $('#specials_canvas');
    },

    /**
     * Changes rendered state flag to false
     *
     * @public
     */
    setUnrendered: () => {

        content = deps = args = $offCanvas = null;
        rendered = false;
    },

    /**
     * Returns the value of the internal
     * rendered state flag
     *
     * @returns {Boolean} Returns true or false
     *
     * @public
     */
    isRendered: () => rendered,

    /**
     * Slug to identify the current page
     */
    id: 'specials',
};

export default api;

